// Admin-only user management. Lists every user, supports inviting new
// accounts (email + role), changing role, and deleting. Backed by the
// orchestrator's /api/v1/admin/users endpoints (RolesGuard `@Roles("admin")`).
//
// User creation is invitation-based: the admin enters an email + role,
// the orchestrator creates the user with no password, mints a one-time
// invite token, and emails a /set-password link via Brevo. Pending
// invitees show a "Resend invite" affordance until they consume the
// token.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	type AdminUserView,
	ApiError,
	httpClient,
	type UserVisibilityFlags,
} from "../../client/http-client";
import { TrashIcon } from "../../components/Icons";

// Per-user opt-in flags for the three debug-level workspace cards.
// Admins see all cards regardless (gating in ProfileDetail short-circuits
// on `isAdmin`); these toggles only affect non-admin users.
const VISIBILITY_TOGGLES: Array<{
	key: keyof UserVisibilityFlags;
	label: string;
	title: string;
}> = [
	{ key: "showLogsCard", label: "Logs", title: "Show the backend Logs card" },
	{
		key: "showPlaygroundCard",
		label: "Playground",
		title: "Show the Playground card",
	},
	{
		key: "showMt5ConnectionCard",
		label: "MT5",
		title: "Show the MT5 connection card",
	},
];

const cardClassName =
	"tg-pane tg-text-primary relative grid w-full max-w-[920px] grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-[18px] shadow-[var(--shadow-tg-elevated)]";
const inputClassName =
	"tg-input tg-text-primary h-10 w-full rounded-[12px] px-3 text-[0.9rem]";
const selectClassName =
	"tg-select tg-text-primary h-10 rounded-[12px] px-3 text-[0.9rem]";
const primaryButtonClassName =
	"tg-btn-primary inline-flex h-9 items-center justify-center rounded-xl px-4 text-[0.85rem] font-semibold disabled:cursor-wait disabled:opacity-60";
const secondaryButtonClassName =
	"tg-btn-secondary inline-flex h-9 items-center rounded-xl px-3 text-[0.82rem] font-semibold disabled:opacity-55";

interface UserManagementModalProps {
	isOpen: boolean;
	onClose: () => void;
	currentUserId: string;
}

export const UserManagementModal = ({
	isOpen,
	onClose,
	currentUserId,
}: UserManagementModalProps) => {
	const [users, setUsers] = useState<AdminUserView[]>([]);
	const [loading, setLoading] = useState(false);
	const [busyForId, setBusyForId] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	// `null` when no delete is pending; otherwise the user the admin is
	// confirming. Drives an in-app confirmation panel inline with the row.
	const [pendingDelete, setPendingDelete] = useState<AdminUserView | null>(
		null,
	);

	// Invite-user form state.
	const [draftEmail, setDraftEmail] = useState("");
	const [draftRole, setDraftRole] = useState<"user" | "admin">("user");
	const [creating, setCreating] = useState(false);

	const refresh = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const next = await httpClient.admin.users.list();
			setUsers(next);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!isOpen) return;
		void refresh();
	}, [isOpen, refresh]);

	useEffect(() => {
		if (!isOpen) return;
		const onKey = (ev: KeyboardEvent) => {
			if (ev.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [isOpen, onClose]);

	// Auto-clear the success notice after a few seconds so it doesn't
	// linger over later actions.
	useEffect(() => {
		if (!notice) return;
		const timer = window.setTimeout(() => setNotice(null), 4000);
		return () => window.clearTimeout(timer);
	}, [notice]);

	const handleCreate = useCallback(async () => {
		setError(null);
		setNotice(null);
		const email = draftEmail.trim();
		if (!email) {
			setError("Enter the email address to invite.");
			return;
		}
		setCreating(true);
		try {
			const created = await httpClient.admin.users.create({
				email,
				role: draftRole,
			});
			setUsers((prev) => [...prev, created]);
			setDraftEmail("");
			setDraftRole("user");
			setNotice(
				`Invitation sent to ${created.email}. They’ll receive an email with a link to set their password.`,
			);
		} catch (err) {
			setError(
				err instanceof ApiError
					? err.code === "EMAIL_TAKEN"
						? "An account with that email already exists."
						: err.message
					: String(err),
			);
		} finally {
			setCreating(false);
		}
	}, [draftEmail, draftRole]);

	const handleSetRole = useCallback(
		async (id: string, role: "user" | "admin") => {
			setBusyForId(id);
			setError(null);
			try {
				const updated = await httpClient.admin.users.setRole(id, role);
				setUsers((prev) => prev.map((u) => (u.id === id ? updated : u)));
			} catch (err) {
				setError(err instanceof ApiError ? err.message : String(err));
			} finally {
				setBusyForId(null);
			}
		},
		[],
	);

	const handleSetVisibilityFlag = useCallback(
		async (
			user: AdminUserView,
			flagKey: keyof UserVisibilityFlags,
			next: boolean,
		) => {
			// Optimistic update first so the checkbox feels snappy under
			// debounced/rapid clicking. Snapshot prior state to roll back
			// if the PATCH fails.
			const prior = user.visibilityFlags;
			setUsers((prev) =>
				prev.map((u) =>
					u.id === user.id
						? {
								...u,
								visibilityFlags: { ...u.visibilityFlags, [flagKey]: next },
							}
						: u,
				),
			);
			setBusyForId(user.id);
			setError(null);
			try {
				const updated = await httpClient.admin.users.setVisibilityFlags(
					user.id,
					{ [flagKey]: next },
				);
				setUsers((prev) =>
					prev.map((u) => (u.id === user.id ? updated : u)),
				);
			} catch (err) {
				setUsers((prev) =>
					prev.map((u) =>
						u.id === user.id ? { ...u, visibilityFlags: prior } : u,
					),
				);
				setError(err instanceof ApiError ? err.message : String(err));
			} finally {
				setBusyForId(null);
			}
		},
		[],
	);

	const handleResendInvite = useCallback(async (user: AdminUserView) => {
		setBusyForId(user.id);
		setError(null);
		setNotice(null);
		try {
			await httpClient.admin.users.resendInvite(user.id);
			setNotice(`Invitation re-sent to ${user.email}.`);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setBusyForId(null);
		}
	}, []);

	const requestDelete = useCallback(
		(user: AdminUserView) => {
			if (user.id === currentUserId) {
				setError("You can't delete your own account.");
				return;
			}
			setError(null);
			setPendingDelete(user);
		},
		[currentUserId],
	);

	const cancelDelete = useCallback(() => {
		setPendingDelete(null);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!pendingDelete) return;
		const id = pendingDelete.id;
		setBusyForId(id);
		setError(null);
		try {
			await httpClient.admin.users.delete(id);
			setUsers((prev) => prev.filter((u) => u.id !== id));
			setPendingDelete(null);
		} catch (err) {
			setError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setBusyForId(null);
		}
	}, [pendingDelete]);

	const sortedUsers = useMemo(() => {
		return [...users].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
	}, [users]);

	if (!isOpen) return null;

	return (
		<div
			className="tg-overlay fixed inset-0 z-50 grid place-items-center p-6"
			// biome-ignore lint/a11y/noStaticElementInteractions: click-outside to close
			onClick={(event) => {
				if (event.target === event.currentTarget) onClose();
			}}
		>
			<div
				className={cardClassName}
				style={{ maxHeight: "calc(100vh - 48px)" }}
			>
				<header className="flex items-start justify-between gap-4 border-b border-[color:var(--color-tg-line)] px-6 py-4">
					<div>
						<h2 className="tg-text-primary text-[1.15rem] font-bold">
							User management
						</h2>
						<p className="tg-text-muted mt-1 text-[0.85rem] leading-6">
							Invite teammates, change roles, delete users. Public signup
							is disabled — every account is created here. New users
							receive an email with a link to set their own password.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className={secondaryButtonClassName}
					>
						Close
					</button>
				</header>

				<div className="grid gap-5 overflow-y-auto px-6 py-5">
					<section className="tg-surface rounded-2xl p-4">
						<h3 className="tg-text-primary text-[0.95rem] font-semibold">
							Invite user
						</h3>
						<p className="tg-text-muted mt-1 text-[0.8rem] leading-5">
							We’ll email them a one-time link so they can set their
							password and sign in.
						</p>
						<form
							className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_auto]"
							onSubmit={(event) => {
								event.preventDefault();
								void handleCreate();
							}}
						>
							<input
								className={inputClassName}
								type="email"
								placeholder="email@example.com"
								value={draftEmail}
								onChange={(e) => setDraftEmail(e.target.value)}
								disabled={creating}
								autoComplete="email"
							/>
							<select
								className={selectClassName}
								value={draftRole}
								onChange={(e) =>
									setDraftRole(e.target.value as "user" | "admin")
								}
								disabled={creating}
							>
								<option value="user">User</option>
								<option value="admin">Admin</option>
							</select>
							<button
								type="submit"
								className={primaryButtonClassName}
								disabled={creating}
							>
								{creating ? "Sending…" : "Send invitation"}
							</button>
						</form>
					</section>

					<section className="tg-surface rounded-2xl">
						<header className="flex items-center justify-between border-b border-[color:var(--color-tg-line)] px-4 py-3">
							<h3 className="tg-text-primary text-[0.95rem] font-semibold">
								All users
							</h3>
							<span className="tg-text-muted text-[0.82rem]">
								{loading
									? "Loading…"
									: `${sortedUsers.length} ${sortedUsers.length === 1 ? "user" : "users"}`}
							</span>
						</header>
						<div className="px-4 py-3">
							{sortedUsers.length === 0 && !loading ? (
								<p className="tg-text-muted py-3 text-center text-[0.88rem]">
									No users yet.
								</p>
							) : (
								<ul className="grid divide-y divide-[color:var(--color-tg-line)]">
									{sortedUsers.map((user) => {
										const isSelf = user.id === currentUserId;
										const busy = busyForId === user.id;
										const isPending = !user.passwordSet;
										return (
											<li
												key={user.id}
												className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 py-3"
											>
												<div className="min-w-0">
													<div className="tg-text-primary truncate text-[0.95rem] font-semibold">
														{user.email}
														{isSelf ? (
															<span className="tg-text-muted ml-2 text-[0.78rem] font-normal">
																(you)
															</span>
														) : null}
														{isPending ? (
															<span className="ml-2 inline-flex items-center rounded-full border border-[color:var(--color-tg-status-warning-border)] bg-[color:var(--color-tg-status-warning-bg)] px-2 py-0.5 text-[0.7rem] font-semibold text-[color:var(--color-tg-status-warning-text)] uppercase tracking-wide">
																Pending invite
															</span>
														) : null}
													</div>
													<div className="tg-text-muted truncate font-mono text-[0.74rem]">
														{user.id.slice(0, 8)} ·{" "}
														{new Date(user.createdAt).toLocaleDateString()}
													</div>
												</div>

												<div
													className="flex items-center gap-3"
													title={
														user.role === "admin"
															? "Admins always see all cards regardless of these toggles"
															: undefined
													}
												>
													{VISIBILITY_TOGGLES.map((t) => (
														<label
															key={t.key}
															className="tg-text-muted flex flex-col items-center gap-1 text-[0.7rem] font-semibold uppercase tracking-wide"
															title={t.title}
														>
															<span>{t.label}</span>
															<input
																type="checkbox"
																checked={!!user.visibilityFlags[t.key]}
																disabled={busy}
																onChange={(e) =>
																	void handleSetVisibilityFlag(
																		user,
																		t.key,
																		e.target.checked,
																	)
																}
																className="h-4 w-4 cursor-pointer disabled:cursor-wait disabled:opacity-50"
															/>
														</label>
													))}
												</div>

												<button
													type="button"
													className={secondaryButtonClassName}
													onClick={() => void handleResendInvite(user)}
													disabled={busy || !isPending}
													title={
														isPending
															? "Re-send the invitation email"
															: "User has already set a password"
													}
												>
													Resend invite
												</button>

												<select
													className={selectClassName}
													value={user.role}
													onChange={(e) =>
														void handleSetRole(
															user.id,
															e.target.value as "user" | "admin",
														)
													}
													disabled={busy || isSelf}
													title={
														isSelf ? "You can't change your own role" : ""
													}
												>
													<option value="user">User</option>
													<option value="admin">Admin</option>
												</select>

												<button
													type="button"
													className="tg-btn-danger inline-flex h-9 items-center gap-2 rounded-xl px-3 text-[0.82rem] font-semibold disabled:opacity-55"
													onClick={() => requestDelete(user)}
													disabled={busy || isSelf}
													title={
														isSelf ? "You can't delete your own account" : ""
													}
												>
													<TrashIcon className="h-4 w-4" />
													Delete
												</button>
											</li>
										);
									})}
								</ul>
							)}
						</div>
					</section>

					{notice ? (
						<div
							className="tg-alert-success rounded-xl px-4 py-3 text-[0.88rem]"
							role="status"
						>
							{notice}
						</div>
					) : null}
					{error ? (
						<div
							className="tg-alert-error rounded-xl px-4 py-3 text-[0.88rem]"
							role="alert"
						>
							{error}
						</div>
					) : null}
				</div>

				{pendingDelete ? (
					<div className="absolute inset-0 z-10 grid place-items-center bg-[color:var(--color-tg-overlay)] p-6 backdrop-blur-sm">
						<div
							className="w-full max-w-[440px] rounded-2xl border border-[color:var(--color-tg-line)] p-5 shadow-2xl"
							style={{ backgroundColor: "var(--color-tg-card)" }}
						>
							<h3 className="tg-text-primary text-[1.05rem] font-semibold">
								Delete this user?
							</h3>
							<p className="tg-text-muted mt-2 text-[0.88rem] leading-6">
								<span className="tg-text-primary font-semibold">
									{pendingDelete.email}
								</span>{" "}
								and all of their profiles, sessions, and Telegram credentials
								will be removed from Postgres.
							</p>
							<p className="tg-text-muted mt-2 text-[0.82rem] leading-5">
								The on-disk data dir for their profiles is left intact on the
								orchestrator's PVC — delete it manually if needed.
							</p>
							<div className="mt-4 flex items-center justify-end gap-2">
								<button
									type="button"
									className={secondaryButtonClassName}
									onClick={cancelDelete}
									disabled={busyForId === pendingDelete.id}
								>
									Cancel
								</button>
								<button
									type="button"
									className="tg-btn-danger inline-flex h-9 items-center gap-2 rounded-xl px-4 text-[0.85rem] font-semibold disabled:cursor-wait disabled:opacity-60"
									onClick={() => void confirmDelete()}
									disabled={busyForId === pendingDelete.id}
								>
									<TrashIcon className="h-4 w-4" />
									{busyForId === pendingDelete.id
										? "Deleting…"
										: "Yes, delete user"}
								</button>
							</div>
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};
